import { useEffect, useRef } from "react";
import { registerTools } from "./core.js";
/**
 * Registers a single tool for the lifetime of the component. Unregisters
 * (via AbortSignal) on unmount or when `deps` change.
 */
export function useWebMCPTool(tool, options = {}, deps = []) {
    useWebMCPTools([tool], options, deps);
}
/**
 * Registers a set of tools for the lifetime of the component. Unregisters
 * all of them on unmount or when `deps` change — the common case for a
 * tool set that depends on component state (a filtered view, a selected
 * item) rather than page-lifetime state.
 */
export function useWebMCPTools(tools, options = {}, deps = []) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const controller = new AbortController();
        registerTools(tools, { ...optionsRef.current, signal: controller.signal });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
/**
 * Registers a tool set only while `active` is true, unregistering the
 * moment it becomes false. This is the primitive behind every dynamic,
 * state-dependent tool surface in this repo: selecting an issue exposes
 * issue-scoped tools, an active filter exposes `bulk_update`, an invited
 * reviewer gets a reviewer-only tool set. The registered set changing with
 * live page state — something no static server-side tool list can do — is
 * the single clearest proof of WebMCP leverage a judge can see on camera.
 */
export function useScopedTools(active, tools, options = {}, deps = []) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!active)
            return;
        const controller = new AbortController();
        const resolved = typeof tools === "function" ? tools() : tools;
        registerTools(resolved, { ...optionsRef.current, signal: controller.signal });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, ...deps]);
}
